import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  PawPrint,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingBag,
  Calendar,
  CreditCard,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Crown,
  Store,
  Megaphone,
  FileText,
  BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

export default function ClubDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Check if user is club admin
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Club admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const [summaryRes, txRes, chartRes] = await Promise.all([
          fetch(`${API}/api/accounting/club/summary?period=${period}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/accounting/club/transactions?limit=20`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/accounting/club/revenue-chart?period=${period}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data.summary);
        }
        
        if (txRes.ok) {
          const data = await txRes.json();
          setTransactions(data.transactions || []);
        }
        
        if (chartRes.ok) {
          const data = await chartRes.json();
          setChartData(data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch club data:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, period]);

  // Transaction type config
  const TX_CONFIG = {
    membership: { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-500/20', label: 'Membership' },
    marketplace_sale: { icon: ShoppingBag, color: 'text-green-500', bg: 'bg-green-500/20', label: 'Marketplace' },
    store_sale: { icon: Store, color: 'text-blue-500', bg: 'bg-blue-500/20', label: 'Store' },
    ad_revenue: { icon: Megaphone, color: 'text-purple-500', bg: 'bg-purple-500/20', label: 'Ads' },
    event_ticket: { icon: Calendar, color: 'text-pink-500', bg: 'bg-pink-500/20', label: 'Events' },
    seller_payout: { icon: ArrowDownRight, color: 'text-red-500', bg: 'bg-red-500/20', label: 'Payout' },
    refund: { icon: ArrowDownRight, color: 'text-orange-500', bg: 'bg-orange-500/20', label: 'Refund' }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <PawPrint className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold">Club Finances</h1>
          </div>
          <p className="text-muted-foreground">Track revenue, expenses, and financial health</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {/* Total Revenue */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(summary?.total_revenue)}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(summary?.total_expenses)}
                </p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Payouts & refunds</p>
          </CardContent>
        </Card>

        {/* Net Balance */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Net Balance</p>
                <p className={`text-2xl font-bold ${(summary?.net_balance || 0) >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {formatCurrency(summary?.net_balance)}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Pending</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(summary?.pending_amount)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Awaiting confirmation</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">
            <PieChart className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="transactions">
            <FileText className="w-4 h-4 mr-2" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="reports">
            <BarChart3 className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Revenue Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-primary" />
                  Revenue by Source
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(summary?.breakdown || {}).map(([type, amount]) => {
                    if (amount <= 0) return null;
                    const config = TX_CONFIG[type] || { icon: DollarSign, color: 'text-gray-500', bg: 'bg-gray-100', label: type };
                    const Icon = config.icon;
                    const percentage = summary?.total_revenue > 0 
                      ? ((amount / summary.total_revenue) * 100).toFixed(1) 
                      : 0;
                    
                    return (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${config.bg}`}>
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div>
                            <p className="font-medium">{config.label}</p>
                            <p className="text-xs text-muted-foreground">{percentage}% of total</p>
                          </div>
                        </div>
                        <span className="font-semibold">{formatCurrency(amount)}</span>
                      </div>
                    );
                  })}
                  
                  {Object.keys(summary?.breakdown || {}).filter(k => (summary?.breakdown[k] || 0) > 0).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No revenue data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/admin/marketplace">
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4" />
                      Manage Marketplace
                    </span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/admin/members">
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      View Members
                    </span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/admin/ads">
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4" />
                      Ad Revenue Settings
                    </span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/events/create">
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Create Event
                    </span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>Latest financial transactions</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-3">
                  {transactions.slice(0, 5).map((tx) => {
                    const config = TX_CONFIG[tx.transaction_type] || { icon: DollarSign, color: 'text-gray-500', bg: 'bg-gray-100' };
                    const Icon = config.icon;
                    
                    return (
                      <div 
                        key={tx.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${config.bg}`}>
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No transactions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>All Transactions</CardTitle>
              <CardDescription>Complete transaction history</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-2">
                  {transactions.map((tx) => {
                    const config = TX_CONFIG[tx.transaction_type] || { icon: DollarSign, color: 'text-gray-500', bg: 'bg-gray-100', label: tx.transaction_type };
                    const Icon = config.icon;
                    
                    return (
                      <div 
                        key={tx.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${config.bg}`}>
                            <Icon className={`w-5 h-5 ${config.color}`} />
                          </div>
                          <div>
                            <p className="font-medium">{tx.description}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {config.label}
                              </Badge>
                              <span>•</span>
                              <span>{new Date(tx.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold text-lg ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </p>
                          <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                            {tx.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No transactions yet</p>
                  <p className="text-sm">Financial activity will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Financial Reports</CardTitle>
              <CardDescription>Download and analyze your data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <Button variant="outline" className="h-auto py-4 flex-col">
                  <Download className="w-6 h-6 mb-2" />
                  <span>Monthly Summary</span>
                  <span className="text-xs text-muted-foreground">PDF Report</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col">
                  <Download className="w-6 h-6 mb-2" />
                  <span>Transaction Export</span>
                  <span className="text-xs text-muted-foreground">CSV File</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col">
                  <Download className="w-6 h-6 mb-2" />
                  <span>Tax Report</span>
                  <span className="text-xs text-muted-foreground">Annual Summary</span>
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground text-center pt-4">
                Report generation coming soon. For now, use the Export button to download transaction data.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
